import { type MouseEvent, useState } from 'react';
import LazyLoad from 'react-lazyload';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { useSwipeable } from 'react-swipeable';

import { Image } from '@/components/image/Image';
import type { Hotel } from '@/interfaces';
import { ImageCarouselButton } from '@/components/imageCarouselButton/ImageCarouselButton';
import { ImageCarouselCaption } from '@/components/imageCarouselCaption/ImageCarouselCaption';

export type SearchResultItemImagesProps = Pick<Hotel, 'id' | 'images' | 'name'> & { onClick: () => void };

export const SearchResultItemImages = ({ id, images = [], name, onClick }: SearchResultItemImagesProps) => {
    const [currentImage, setCurrentImage] = useState(0);

    const handleNext = (event?: MouseEvent): void => {
        event?.preventDefault();
        setCurrentImage((currentImage + 1) % images.length);
    };

    const handlePrevious = (event?: MouseEvent): void => {
        event?.preventDefault();
        setCurrentImage(currentImage > 0 ? currentImage - 1 : images.length - 1);
    };

    const swipeHandlers = useSwipeable({
        preventDefaultTouchmoveEvent: true,
        onSwipedLeft: () => handleNext(),
        onSwipedRight: () => handlePrevious(),
    });

    const hasImages = images.length > 0;
    const imageId = hasImages ? currentImage : 'no-image';
    return (
        <div {...swipeHandlers} className="image-container result-list-image" data-testid="searchResultItemImages">
            <LazyLoad>
                <TransitionGroup>
                    <CSSTransition key={`image-transition-${id}-${imageId}`} timeout={300} classNames="fade">
                        <Image
                            key={`image-${id}-${imageId}`}
                            image={images.map((image) => image.large)[currentImage]}
                            frame={false}
                            altText={name}
                            onClick={onClick}
                        />
                    </CSSTransition>
                </TransitionGroup>
            </LazyLoad>
            {hasImages && (
                <>
                    <ImageCarouselButton direction="left" eventHandler={handlePrevious} small showOnHover />
                    <ImageCarouselButton direction="right" eventHandler={handleNext} small showOnHover />
                    <ImageCarouselCaption currentImage={currentImage + 1} numberOfImages={images.length} small showOnHover />
                </>
            )}
        </div>
    );
};
